import orderByLodash from 'lodash.orderby';

import { type TAtpCourseData, type TAtpOptionalCourseData } from '@/_models';

import courseImages from './actions/course-images';

export const isEntranceExam = (product_type: string) =>
  product_type.toLocaleLowerCase().indexOf('entrance exam prep course') > -1;

export const isLicensingExam = (product_type: string) =>
  product_type.toLocaleLowerCase().indexOf('licensing exam prep course') > -1;

export const isOnDemandTutoring = (product_type: string) =>
  product_type.toLocaleLowerCase().indexOf('on demand tutoring') > -1;

export const isFundamentalCourse = (product_type: string) =>
  product_type.toLocaleLowerCase().indexOf('fundamental courses') > -1;

export const isGenEdCourse = (product_type: string) =>
  product_type.toLocaleLowerCase().indexOf('gen ed prep course') > -1;

const courseNameConverter = (name: string) =>
  name
    .toLowerCase()
    .split(' ')
    .join('_')
    .split('&')
    .join('_')
    .split('/')
    .join('_')
    .split(')')
    .join('')
    .split('(')
    .join('')
    .split('-')
    .join('_')
    .split('___')
    .join('_')
    .split('__')
    .join('_');

export function getCoursesByCategories(
  optional_courses: TAtpOptionalCourseData[],
  courses_list_taken_at_achieve: TAtpCourseData[]
) {
  const entranceExamCourses = optional_courses
    .filter((c) => isEntranceExam(c.product_type || ''))
    .map(
      (c) =>
        ({
          ...c,
          image: '/checkout-courses/entrance_exam.jpg',
          description: 'Get in to college by passing your Entrance Exam, like TEAS, HESI, or PAX on the first try.',
          college_course_name: undefined,
        }) as TAtpOptionalCourseData
    );

  const licensingExamCourses = optional_courses
    .filter((c) => isLicensingExam(c.product_type || ''))
    .map((c) => {
      let image = courseNameConverter(c.name);
      image = courseImages.includes(image) ? `/checkout-courses/${image}.jpg` : '/checkout-courses/licensing_exam.jpg';

      return {
        ...c,
        image,
        description:
          'Pass your Licensing Exam, like NCLEX or NBDHE, on the first try so you’re fully certified to enter the workforce.',
        college_course_name: undefined,
      } as TAtpOptionalCourseData;
    });

  const optionalSelectableCourses: TAtpOptionalCourseData[] = [
    ...(entranceExamCourses ?? []),
    ...(licensingExamCourses ?? []),
  ];

  const odtSelectableCourses = optional_courses
    .filter((c) => isOnDemandTutoring(c.product_type || ''))
    .map((c) => {
      let image = courseNameConverter(c.name);
      image = courseImages.includes(image) ? `/checkout-courses/${image}.jpg` : '/checkout-courses/tutoring.jpg';

      return {
        ...c,
        image,
        description:
          'Pass your Licensing Exam, like NCLEX or NBDHE, on the first try so you’re fully certified to enter the workforce.',
        college_course_name: undefined,
      } as TAtpOptionalCourseData;
    });

  const _atpSelectableCourses = orderByLodash(courses_list_taken_at_achieve, ['name'], ['asc']).map((c) => {
    let image = courseNameConverter(c.name);
    image = courseImages.includes(image) ? `/checkout-courses/${image}.jpg` : '/checkout-courses/generic.jpg';
    return { ...c, image };
  });

  const fundamentalCoursesOnly = _atpSelectableCourses.filter((c) => isFundamentalCourse(c.product_type || ''));
  const atpSelectableCoursesOnly = _atpSelectableCourses.filter((c) => !isFundamentalCourse(c.product_type || ''));

  // fundamental  courses + prep courses + (license + entrance) + ODT
  const courses = [
    ...atpSelectableCoursesOnly,
    ...fundamentalCoursesOnly,
    ...optionalSelectableCourses,
    ...odtSelectableCourses,
  ];

  const atpSelectableCourses = [...atpSelectableCoursesOnly, ...fundamentalCoursesOnly];

  return {
    entranceExamCourses,
    licensingExamCourses,
    optionalSelectableCourses,
    odtSelectableCourses,
    atpSelectableCourses,
    fundamentalCoursesOnly,
    atpSelectableCoursesOnly,
    courses,
  };
}

type TCourseTypes = ReturnType<typeof getCoursesByCategories>;

export function getCourseIds({
  optionalSelectableCourses,
  odtSelectableCourses,
  fundamentalCoursesOnly,
  atpSelectableCoursesOnly,
  courses,
}: TCourseTypes) {
  const courseIdsAsSelected = [...courses.map((course) => course.id)];

  const planPrepCourseIds = atpSelectableCoursesOnly.map((course) => course.id);
  const planAddonCourseIds = optionalSelectableCourses.map((course) => course.id);
  const planOdtCourseIds = odtSelectableCourses.map((course) => course.id);
  const planFundamentalCourseIds = fundamentalCoursesOnly.map((course) => course.id);

  return {
    courseIdsAsSelected,
    planPrepCourseIds,
    planAddonCourseIds,
    planOdtCourseIds,
    planFundamentalCourseIds,
  };
}
